import { z } from "zod";

export const apiEnvSchema = z.object({
  NODE_ENV: z
    .enum(["development", "production", "test"])
    .default("development"),
  PORT: z.coerce.number().int().positive().default(4000),
  DATABASE_URL: z.string().min(1),
  REDIS_URL: z.string().min(1),
  WEB_BASE_URL: z.string().url(),
  EMPLOYER_URL: z.string().url().optional(),
  ADMIN_URL: z.string().url().optional(),
  ALLOWED_ORIGINS: z.string().optional(),
  API_BASE_URL: z.string().url().optional(),
  COOKIE_DOMAIN: z.string().optional(),
  REVIEW_EMAILS: z.string().optional(),
  REVIEW_OTP_CODE: z.string().min(4).default("123456"),
  JWT_ACCESS_SECRET: z.string().min(16),
  SEPAY_MERCHANT_ID: z.string().min(1).optional(),
  SEPAY_SECRET_KEY: z.string().min(1).optional(),
  SEPAY_IPN_URL: z.string().url().optional(),
  SEPAY_IPN_SECRET_KEY: z.string().min(1).optional(),
  SEPAY_RETURN_URL: z.string().url().optional(),
  SEPAY_CANCEL_URL: z.string().url().optional(),
  SEPAY_ERROR_URL: z.string().url().optional(),
  SEPAY_ENV: z.enum(["sandbox", "production"]).default("sandbox"),
  // Casso webhook (new payment flows: subscription / sourcing / boost). Signs
  // requests as `x-casso-signature: t=<ms>,v1=<hmac-sha256>`. Get the secret
  // from the Casso dashboard → Webhook settings.
  CASSO_WEBHOOK_SECRET: z.string().min(1).optional(),
  // Casso sync API (https://oauth.casso.vn/v2/sync) — asks Casso to pull new bank
  // transactions on demand (so we don't wait for its polling cycle). CASSO_API_KEY
  // authenticates as `Apikey <key>`; CASSO_BANK_ACC_ID is the linked bank account
  // number on Casso. Both from the Casso dashboard.
  CASSO_API_KEY: z.string().min(1).optional(),
  CASSO_BANK_ACC_ID: z.string().min(1).optional(),
  // VietQR.io (https://vietqr.io) — generates the bank-transfer QR customers
  // scan to pay a PLAN-<seq> order. Credentials from My VietQR. The receiving
  // account is fixed for the whole system.
  VIETQR_CLIENT_ID: z.string().min(1).optional(),
  VIETQR_API_KEY: z.string().min(1).optional(),
  VIETQR_ACCOUNT_NO: z.string().min(6).max(19).optional(),
  VIETQR_ACCOUNT_NAME: z.string().min(5).max(50).optional(),
  VIETQR_ACQ_ID: z.string().length(6).optional(), // bank BIN, 6 digits
  VIETQR_TEMPLATE: z.string().default("compact"),
  SEPAY_SIMULATE: z
    .preprocess((v) => {
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "boolean") return v;
      const s = String(v).toLowerCase().trim();
      return s === "true" || s === "1" || s === "yes";
    }, z.boolean())
    .default(false),
  ESMS_SIMULATE: z
    .preprocess((v) => {
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "boolean") return v;
      const s = String(v).toLowerCase().trim();
      return s === "true" || s === "1" || s === "yes";
    }, z.boolean())
    .default(false),
  ESMS_API_KEY: z.string().optional(),
  ESMS_SECRET_KEY: z.string().optional(),
  ESMS_BRANDNAME: z.string().optional(),
  // eSMS SmsType. 2 = CSKH Brandname (cần Brandname đã duyệt).
  // 4 = đầu số cố định 8755 (không cần brandname). 8 = đầu số ngẫu nhiên.
  // Mặc định 4 nếu không có Brandname, 2 nếu có.
  ESMS_SMS_TYPE: z.string().optional(),
  ESMS_ZNS_OA_ID: z.string().optional(),
  ESMS_ZNS_TEMPLATE_ID_OTP: z.string().optional(),
  ESMS_VOICE_TEMPLATE_ID: z.string().optional(),
  OTP_CASCADE_ORDER: z.string().default("ZNS,SMS,VOICE"),
  // --- Phone OTP via Zalo OA (ZNS) — SCRUM-69 ---
  // We send phone OTP directly through a Zalo Official Account's ZNS API (not a
  // reseller). PhoneModule builds senders from PHONE_OTP_PROVIDERS in order and
  // fails over on error; only ZALO_ZNS is implemented today.
  PHONE_OTP_PROVIDERS: z.string().default("ZALO_ZNS"),
  // Kill-switch for ALL outbound phone OTP. When true, PhoneModule uses the
  // log-only sender (codes go to stdout, nothing delivered). Accepts true/1/yes.
  PHONE_OTP_DISABLED: z
    .preprocess((v) => {
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "boolean") return v;
      const s = String(v).toLowerCase().trim();
      return s === "true" || s === "1" || s === "yes";
    }, z.boolean())
    .default(false),
  // Zalo OA app credentials (Zalo for Developers → your app). app_id + secret_key
  // are used to REFRESH the OA access token; oaId keys the persisted ZaloOaToken
  // row. Required for real ZNS delivery; absent → log-only sender.
  ZALO_APP_ID: z.string().optional(),
  ZALO_APP_SECRET: z.string().optional(),
  ZALO_OA_ID: z.string().optional(),
  // ZNS OTP template id (created in the Zalo OA console AFTER authorization).
  // The approved template must expose an `otp` param. Absent → log-only sender.
  ZALO_ZNS_TEMPLATE_ID_OTP: z.string().optional(),
  // One-time seed of the OA token pair obtained manually via Zalo API Explorer.
  // On boot, if no ZaloOaToken row exists yet, ZaloOaTokenService imports these
  // into the DB and then self-manages refresh (each refresh rotates both tokens,
  // so these envs go stale after first refresh — the DB row is the source of
  // truth thereafter). Safe to leave set; they're only read when the row is
  // missing.
  ZALO_OA_ACCESS_TOKEN_SEED: z.string().optional(),
  ZALO_OA_REFRESH_TOKEN_SEED: z.string().optional(),
  ENCRYPTION_KEY_V1: z.string().optional(),
  RESEND_API_KEY: z.string().optional(),
  // Accepts plain "no-reply@chotca.com" or display-name "Chốt Ca <no-reply@chotca.com>".
  // Transactional From (OTP, verify, reset, KYC, interview) — keep on a clean,
  // low-volume sending domain so bulk/marketing reputation can't block logins.
  EMAIL_FROM: z.string().min(1).optional(),
  // Marketing/bulk From (digests, recommendations, promos, cold claim invites).
  // Use a SEPARATE sending domain (e.g. go.chotca.com) so a Gmail rate-limit on
  // bulk mail never starves transactional delivery. Falls back to EMAIL_FROM.
  EMAIL_FROM_MARKETING: z.string().min(1).optional(),
  // Kill-switch for ALL outbound email. When true, every send — OTP, verify,
  // reset, KYC, interview AND every bulk/marketing message (digests,
  // recommendations, employer, promos, cold claim invites) — is suppressed and
  // the log-only provider is used, so NOTHING is delivered. Use to fully stop
  // sending during a domain block / rate-limit. Accepts true/1/yes; unset or
  // false = send everything. (Note: this also stops OTP, so logins break while on.)
  EMAIL_DISABLED: z
    .preprocess((v) => {
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "boolean") return v;
      const s = String(v).toLowerCase().trim();
      return s === "true" || s === "1" || s === "yes";
    }, z.boolean())
    .default(false),
  // Kill-switch for ALL outbound FCM push. When true, FcmService.sendToUser is a
  // no-op — Firebase Admin still initializes (so Google idToken verify / phone
  // auth keep working), only push delivery is suppressed. Use on dev/staging
  // that runs on real prod data so notifications don't reach real devices.
  // Accepts true/1/yes; unset or false = push normally.
  PUSH_DISABLED: z
    .preprocess((v) => {
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "boolean") return v;
      const s = String(v).toLowerCase().trim();
      return s === "true" || s === "1" || s === "yes";
    }, z.boolean())
    .default(false),
  // Switch for boost emails to matched workers who have no push device. Off by
  // default; turning it off stops new boost emails immediately (boosts still
  // push). Accepts true/1/yes; unset or false = push only.
  BOOST_EMAIL_ENABLED: z
    .preprocess((v) => {
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "boolean") return v;
      const s = String(v).toLowerCase().trim();
      return s === "true" || s === "1" || s === "yes";
    }, z.boolean())
    .default(false),
  // Svix signing secret for the Resend webhook (whsec_...). When unset, the
  // webhook rejects all requests in production (fail closed).
  RESEND_WEBHOOK_SECRET: z.string().optional(),
  // --- Gmail API sending (Google Workspace) ---
  // When GMAIL_SENDER is set, outbound email goes through the Gmail API instead
  // of Resend, using a service account with domain-wide delegation (scope
  // https://www.googleapis.com/auth/gmail.send) that impersonates this Workspace
  // mailbox (e.g. "no-reply@chotca.com"). Best for low-volume transactional/OTP
  // mail — Workspace caps sending at ~2,000 messages/day and consumer @gmail.com
  // is NOT supported (no domain-wide delegation). The From header uses EMAIL_FROM
  // (must be a verified send-as of GMAIL_SENDER), falling back to GMAIL_SENDER.
  GMAIL_SENDER: z.string().optional(),
  // Service account JSON (base64) authorized for domain-wide delegation with the
  // gmail.send scope. Falls back to FIREBASE_SERVICE_ACCOUNT_BASE64 / _PATH when
  // unset (reuse the Firebase SA only if you grant it gmail.send DWD in Admin).
  GMAIL_SERVICE_ACCOUNT_BASE64: z.string().optional(),
  // Hard cap on messages sent through the Gmail API per rolling 24h (counted in
  // Redis). Once reached, GmailEmailProvider refuses further sends — a HARD STOP,
  // so OTP/login email will fail until the window rolls off — to avoid tripping
  // Google's ~2,000/day lock (which would block the mailbox for 24h). Keep it
  // safely under 2000. 0 disables the cap.
  GMAIL_DAILY_LIMIT: z.coerce.number().int().min(0).default(1500),
  R2_ACCOUNT_ID: z.string().optional(),
  R2_ACCESS_KEY_ID: z.string().optional(),
  R2_SECRET_ACCESS_KEY: z.string().optional(),
  R2_BUCKET_PUBLIC: z.string().optional(),
  R2_BUCKET_KYC_PRIVATE: z.string().optional(),
  R2_PUBLIC_BASE: z.string().optional(),
  SENTRY_DSN_API: z.string().min(1).optional(),
  AXIOM_TOKEN: z.string().optional(),
  AXIOM_DATASET: z.string().optional(),
  FIREBASE_PROJECT_ID: z.string().optional(),
  // Base64-encoded service account JSON (entire file). Decoded once at FCM
  // service init. Optional → if missing, push fan-out is no-op (DB rows still
  // written so in-app notification list works).
  FIREBASE_SERVICE_ACCOUNT_BASE64: z.string().optional(),
  // Path to service account JSON file. Dev convenience — takes precedence over
  // FIREBASE_SERVICE_ACCOUNT_BASE64 when set. Repo default: ./service_account.json
  // (gitignored). In prod use the BASE64 form via secret manager.
  FIREBASE_SERVICE_ACCOUNT_PATH: z.string().optional(),
  // Which OTP provider runs the worker login flow.
  //   esms     — legacy ZNS/SMS/VOICE cascade (needs eSMS brandname)
  //   firebase — Firebase Phone Auth (mobile sends SMS via Firebase, backend only verifies idToken)
  OTP_PROVIDER: z.enum(["esms", "firebase"]).default("esms"),
  // ── Social login (Google + Apple), docs/03-auth.md ──────────────────────────────
  // ALL optional so the API still boots before secrets are configured. When a
  // provider's config is missing, POST /auth/oauth/verify returns a clean
  // "not configured" error instead of crashing startup. The email-OTP path is
  // untouched, so an older mobile build keeps working regardless of these.
  //
  // Google is verified via the FIREBASE broker (the client signs into Firebase
  // Auth and posts a Firebase ID token, verified by firebase-admin using
  // FIREBASE_PROJECT_ID + the service account above). The two vars below are
  // RESERVED — only needed if Google is switched back to direct token verify.
  GOOGLE_OAUTH_CLIENT_ID_WEB: z.string().optional(),
  GOOGLE_OAUTH_CLIENT_ID_IOS: z.string().optional(),
  // Apple: native identity token `aud` == the app bundle id (APPLE_CLIENT_ID).
  // The Team ID / Key ID / .p8 private key sign the client_secret JWT used to
  // exchange the authorization code and to revoke on account deletion (TN3194).
  // APPLE_PRIVATE_KEY accepts the raw .p8 PEM or a base64 of it.
  APPLE_CLIENT_ID: z.string().optional(),
  APPLE_TEAM_ID: z.string().optional(),
  APPLE_KEY_ID: z.string().optional(),
  APPLE_PRIVATE_KEY: z.string().optional(),
  // Goong (goong.io) API key — powers the address autocomplete/typeahead in the
  // employer address picker (rsapi.goong.io). Goong is a Vietnam-focused maps
  // provider: excellent VN address data, no separate-billing / prepay hoops
  // (the reason we dropped Google Places here). OPTIONAL so the API still boots
  // without it: when unset, /geocode/autocomplete and /geocode/place-details
  // return a clean "not configured" 503 and the picker silently falls back to
  // the free Google-Maps-link paste flow.
  GOONG_API_KEY: z.string().optional(),
  // Worker per-day apply quota. Defaults match the P1 spec (10 / 50 KYC).
  APPLY_LIMIT_NO_KYC: z.coerce.number().int().positive().default(10),
  APPLY_LIMIT_KYC: z.coerce.number().int().positive().default(50),
  // Max concurrent OPEN postings per org (anti-spam). Postings auto-expire after
  // 30 days, so this bounds how many live tin an org can keep on the feed at once.
  // Verified tier = org.kycStatus APPROVED. Admin-import / shadow orgs are exempt.
  OPEN_POSTING_LIMIT_UNVERIFIED: z.coerce.number().int().positive().default(10),
  OPEN_POSTING_LIMIT_VERIFIED: z.coerce.number().int().positive().default(20),
  // Employer signup-bonus credits granted on org create (default 3).
  EMPLOYER_SIGNUP_BONUS: z.coerce.number().int().nonnegative().default(3),
  // Employer first-time KYC-bonus credits granted on org KYC approval (default 10).
  EMPLOYER_VERIFY_BONUS: z.coerce.number().int().nonnegative().default(10),
  // Credits granted when a shadow org is connected/claimed via invite (default 5).
  // Independent of (and additive to) the later KYC-approval bonus.
  EMPLOYER_CLAIM_BONUS: z.coerce.number().int().nonnegative().default(5),
  // TTL (hours) for claim invitations auto-sent when a shadow org's first
  // applicant arrives. Default 14 days.
  AUTO_CLAIM_TTL_HOURS: z.coerce.number().int().positive().default(336),
});

export type ApiEnv = z.infer<typeof apiEnvSchema>;

type EnvMap = Record<string, string | undefined>;

function defaultProcessEnv(): EnvMap {
  const proc = (globalThis as { process?: { env?: EnvMap } }).process;
  return proc?.env ?? {};
}

export function parseApiEnv(env: EnvMap = defaultProcessEnv()): ApiEnv {
  const normalized: EnvMap = { ...env };
  for (const key of Object.keys(normalized)) {
    const v = normalized[key];
    if (v === undefined || v === "") {
      delete normalized[key];
    } else if (typeof v === "string" && v.trim() === "") {
      delete normalized[key];
    }
  }
  const r2 = normalized["R2_PUBLIC_BASE"];
  if (r2) {
    try {
      new URL(r2);
    } catch {
      delete normalized["R2_PUBLIC_BASE"];
    }
  }
  return apiEnvSchema.parse(normalized);
}
